import React, { useState, useCallback, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  FlatList,
  TouchableOpacity,
  RefreshControl,
} from 'react-native';
import { useNavigation, useRoute, RouteProp, useFocusEffect } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { Ionicons } from '@expo/vector-icons';
import Svg, { Path } from 'react-native-svg';
import { KPIList } from '../../types';
import { COLORS, FONTS, LABELS, STATUS_COLORS } from '../../constants/config';
import { EmptyState, Badge, ProgressBar, PageContainer, Navbar, Breadcrumb, KPIsSkeleton, SKELETON_DIMENSIONS, ViewOnlyBadge } from '../../components';
import PendingDeletionBadge from '../../components/PendingDeletionBadge';
import { MainStackParamList } from '../../navigation/MainNavigator';
import { useResponsive, useKPIsQuery, useRoleNavigation, useAutoTranslate } from '../../hooks';
import { useQueryClient } from '@tanstack/react-query';
import { useNotification } from '../../contexts/NotificationContext';
import { downloadBlobAsFile } from '../../utils/downloadFile';
import { useTheme } from '../../contexts/ThemeContext';
import { kpisApi } from '../../services/apiService';
import { useLanguage } from '../../contexts/LanguageContext';
import { getTextAlign } from '../../utils/rtl';
import { useYear } from '../../contexts/YearContext';

import { tokens } from '@design';
import { Button } from '@design/components/Button';
const ExportIcon = ({ size = 16, color = tokens.colors.raw.gray["500"] }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15M17 8L12 3M12 3L7 8M12 3V15" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

const FileInputIcon = ({ size = 16, color = tokens.colors.semantic.general.foreground }: { size?: number; color?: string }) => (
  <Svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    <Path d="M4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V7L15 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V8" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M14 2V8H20" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M2 15H10" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <Path d="M8 13L10 15L8 17" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </Svg>
);

type RouteProps = RouteProp<MainStackParamList, 'KPIs'>;
type NavigationProp = NativeStackNavigationProp<MainStackParamList>;

export default function KPIsScreen() {
  const navigation = useNavigation<NavigationProp>();
  const route = useRoute<RouteProps>();
  const { showSuccess, showError } = useNotification();
  const { organizationId, goalId, initiativeId } = route.params || {};
  const { isMobile, isTablet, isDesktop, isSmallMobile, containerPadding, cardPadding, sectionSpacing } = useResponsive();
  const { isViewOnly } = useRoleNavigation();
  const { colors, isDark } = useTheme();
  const { t, language } = useLanguage();
  const { translate, translateOrg } = useAutoTranslate();
  const { selectedYear } = useYear();

  const themedCardStyles = {
    card: { backgroundColor: colors.card, borderColor: colors.cardBorder },
    cardTitle: { color: colors.textPrimary },
    cardValue: { color: colors.text },
    cardDescription: { color: colors.textMuted },
    sectionTitle: { color: colors.textPrimary },
    pageTitle: { color: colors.gray900 },
    pageSubtitle: { color: colors.textMuted },
  };

  // Filter state
  const [selectedType, setSelectedType] = useState<string>('');
  const [selectedSource, setSelectedSource] = useState<string>('');

  // Pagination state
  const pageSize = 20;
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [refreshing, setRefreshing] = useState(false);

  const queryClient = useQueryClient();

  const {
    data: kpisData,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useKPIsQuery({
    page: currentPage,
    pageSize: pageSize,
    sourceType: selectedSource,
    source: undefined,
    initiative: initiativeId,
    strategicGoal: goalId,
    year: selectedYear,
  });

  const kpis = kpisData?.results || [];

  // Auto-refresh when screen regains focus (e.g. after add/edit)
  useFocusEffect(
    useCallback(() => {
      refetch();
    }, [refetch])
  );

  // Update total pages when data changes
  React.useEffect(() => {
    if (kpisData?.count) {
      setTotalPages(Math.ceil(kpisData.count / pageSize));
    }
  }, [kpisData?.count, pageSize]);

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    await refetch();
    setRefreshing(false);
  }, [refetch]);

  // State for export loading
  const [isExporting, setIsExporting] = useState(false);

  // Handle Excel export - fetch ALL data, not just current page
  const handleExportExcel = async () => {
    if (kpisData?.count === 0) {
      showError(t('kpis.noKPIsToExport'));
      return;
    }

    setIsExporting(true);
    try {
      // Backend builds the report (bilingual + target grid), honouring the same filters.
      const params: Record<string, any> = { lang: language };
      if (selectedSource) params.source_type = selectedSource;
      if (initiativeId) params.initiative = initiativeId;
      if (goalId) params.strategic_goal = goalId;

      const response = await kpisApi.exportExcel(params);
      downloadBlobAsFile(response.data, `${t('exportFiles.kpis')}.xlsx`);
      showSuccess(t('kpis.exportSuccess', { count: kpisData?.count ?? 0 }));
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      showError(t('kpis.exportError'));
    } finally {
      setIsExporting(false);
    }
  };

  const getStatusColor = (percentage: number) => {
    if (percentage >= 80) return 'success';
    if (percentage >= 50) return 'warning';
    return 'danger';
  };

  const renderItem = ({ item }: { item: KPIList }) => (
    <TouchableOpacity
      style={[
        styles.card,
        themedCardStyles.card,
        { padding: cardPadding, minHeight: SKELETON_DIMENSIONS.KPI_CARD_HEIGHT }
      ]}
      onPress={() => navigation.navigate('KPIDetail', { id: item.id })}
    >
      <View style={styles.cardHeader}>
        <View style={[styles.iconContainer, { backgroundColor: (item.kpi_type === 'strategic' ? COLORS.primary : COLORS.secondary) + '15' }]}>
          <Ionicons
            name="stats-chart"
            size={isSmallMobile ? 20 : 24}
            color={item.kpi_type === 'strategic' ? COLORS.primary : COLORS.secondary}
          />
        </View>
        <View style={styles.cardInfo}>
          <Text style={[styles.title, themedCardStyles.cardTitle, isSmallMobile && { fontSize: tokens.typography.paragraph["paragraph small font size"] }]} numberOfLines={2}>{translate(item.name)}</Text>
          <View style={styles.meta}>
            <Badge text={translate(item.kpi_type_display)} variant={item.kpi_type === 'strategic' ? 'primary' : 'secondary'} size="small" />
            <Text style={[styles.frequency, themedCardStyles.cardDescription]}>{translate(item.frequency_display)}</Text>
            {item.has_pending_deletion && <PendingDeletionBadge compact />}
          </View>
        </View>
        <Ionicons name="chevron-back" size={24} color={colors.textSecondary} />
      </View>

      <View style={[styles.cardBody, { padding: isSmallMobile ? 12 : 16, paddingTop: tokens.spacing.absolute["0"] }]}>
        <View style={styles.valuesRow}>
          <View style={styles.valueItem}>
            <Text style={[styles.valueLabel, themedCardStyles.cardDescription]}>{LABELS.target}</Text>
            <Text style={[styles.valueText, themedCardStyles.cardValue, isSmallMobile && { fontSize: tokens.typography.paragraph["paragraph small font size"] }]}>{item.target_value} {item.unit}</Text>
          </View>
          <View style={styles.valueItem}>
            <Text style={[styles.valueLabel, themedCardStyles.cardDescription]}>{LABELS.actual}</Text>
            <Text style={[styles.valueText, themedCardStyles.cardValue, isSmallMobile && { fontSize: tokens.typography.paragraph["paragraph small font size"] }]}>{item.latest_value || '-'} {item.unit}</Text>
          </View>
        </View>
        <View style={styles.progressRow}>
          <ProgressBar value={item.achievement_percentage} />
        </View>
      </View>

      {item.department_name && (
        <View style={[styles.cardFooter, { borderTopColor: colors.cardBorder }]}>
          <Text style={[styles.orgName, themedCardStyles.cardDescription]}>{translateOrg(item.department_name)}</Text>
        </View>
      )}
    </TouchableOpacity>
  );

  if (isLoading && kpis.length === 0) {
    return (
      <PageContainer>
        <Navbar />
        <KPIsSkeleton />
      </PageContainer>
    );
  }

  if (error) {
    return (
      <EmptyState
        icon="alert-circle-outline"
        title={t('common.error')}
        message={error.message}
        actionLabel={t('common.refresh')}
        onAction={refetch}
      />
    );
  }

  if (kpis.length === 0) {
    return (
      <EmptyState
        icon="stats-chart-outline"
        title={t('common.noData')}
        message={t('kpis.noKPIs')}
      />
    );
  }

  return (
    <PageContainer>
      <Navbar />
      {/* Breadcrumb with sidebar toggle */}
      <Breadcrumb
        items={[
          { label: t('navigation.home') },
          { label: t('kpis.title') },
        ]}
      />
      {/* Header with export button and ViewOnlyBadge */}
      <View style={styles.headerRow}>
        <View style={{ flexDirection: 'row', gap: tokens.spacing.absolute["2"] }}>
          {/* Export Button - Left side */}
          <Button
            variant="secondary"
            onPress={handleExportExcel}
            trailingIcon={<ExportIcon size={16} color={colors.textSecondary} />}
          >
            {isMobile ? t('common.export') : t('common.exportExcel')}
          </Button>
          {!isViewOnly && (
            <Button
              variant="secondary"
              onPress={() => navigation.navigate('ImportKPIs' as any)}
              trailingIcon={<FileInputIcon size={16} color={colors.textPrimary} />}
            >
              {isMobile ? t('importKPIs.short') : t('importKPIs.title')}
            </Button>
          )}
        </View>

        {/* Title - Right side */}
        <View style={styles.titleContainer}>
          <Text style={[styles.pageTitle, themedCardStyles.pageTitle]}>{t('kpis.title')}</Text>
          {isViewOnly && <ViewOnlyBadge />}
        </View>
      </View>
      <FlatList
        data={kpis}
        renderItem={renderItem}
        keyExtractor={(item) => item.id.toString()}
        contentContainerStyle={[
          styles.list,
          { padding: containerPadding, minHeight: 600 }
        ]}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
      />
    </PageContainer>
  );
}

const getStyles = (isRTL: boolean) => StyleSheet.create({
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: tokens.spacing.absolute["4"],
    paddingVertical: tokens.spacing.absolute["3"],
  },
  titleContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.absolute["3"],
  },
  pageTitle: {
    fontSize: tokens.typography["heading 4"]["font size"],
    fontWeight: '700',
    color: tokens.colors.semantic.general.foreground,
    textAlign: getTextAlign(isRTL),
  },
  list: {
    padding: tokens.spacing.absolute["4"],
  },
  card: {
    backgroundColor: COLORS.white,
    borderRadius: tokens.radii.absolute["radius-12"],
    marginBottom: tokens.spacing.absolute["3"],
    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
    elevation: 3,
    overflow: 'hidden',
  },
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    padding: tokens.spacing.absolute["4"],
    overflow: 'hidden',
  },
  iconContainer: {
    width: 44,
    height: 44,
    borderRadius: tokens.radii.absolute["radius-10"],
    justifyContent: 'center',
    alignItems: 'center',
    marginStart: tokens.spacing.absolute["3"],
  },
  cardInfo: {
    flex: 1,
    alignItems: 'flex-end',
    overflow: 'hidden',
  },
  title: {
    fontSize: tokens.typography.paragraph["paragraph font size"],
    fontWeight: '600',
    color: COLORS.text,
    textAlign: getTextAlign(isRTL),
    marginBottom: tokens.spacing.absolute["2"],
  },
  meta: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
  },
  frequency: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    color: COLORS.textMuted,
    marginEnd: tokens.spacing.absolute["2"],
  },
  cardBody: {
    padding: tokens.spacing.absolute["4"],
    paddingTop: tokens.spacing.absolute["0"],
  },
  valuesRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: tokens.spacing.absolute["3"],
    overflow: 'hidden',
  },
  valueItem: {
    alignItems: 'center',
  },
  valueLabel: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    color: COLORS.textMuted,
    marginBottom: tokens.spacing.absolute["1"],
  },
  valueText: {
    fontSize: tokens.typography.paragraph["paragraph font size"],
    fontWeight: '600',
    fontFamily: FONTS.numbers,
    color: COLORS.text,
  },
  progressRow: {
    marginTop: tokens.spacing.absolute["2"],
  },
  cardFooter: {
    borderTopWidth: tokens.spacing.absolute["px"],
    borderTopColor: COLORS.border,
    padding: tokens.spacing.absolute["3"],
  },
  orgName: {
    fontSize: tokens.typography.paragraph["paragraph mini font size"],
    color: COLORS.textMuted,
    textAlign: getTextAlign(isRTL),
  },
});

const styles = getStyles(true);
